// The persistence seam. Every component that needs to save/open/autosave a
// project calls through here, never through the adapters directly. It now
// dispatches by session mode:
//
//   - local mode (signed out, or backend unreachable): continuous autosave to
//     IndexedDB, exactly as before.
//   - remote mode (signed in): continuous autosave to the backend, keyed by the
//     currently open remote project id (see remote-api-adapter.js).
//
// Explicit file Save/Open stays local in both modes: a .pandemonium.json on disk
// is the portable backup path and does not depend on an account. Swapping the
// backend for another (a different API, or Firebase) is still just another
// adapter behind these functions, with no component or store change.
'use strict';

import { saveProjectToFile, parseProjectFileText } from './local-file-adapter.js';
import { saveCurrentProjectLocally, loadCurrentProjectLocally, clearCurrentProjectLocally } from './local-db.js';
import { saveProjectRemote, loadProjectRemote } from './remote-api-adapter.js';
import { fetchSharedProjection } from './sharing-adapter.js';
import { readFileAsText } from '../utils/files.js';
import { session } from './session.js';
import { trackProjectSave } from '../utils/analytics.js';

// File save/open: always local, both modes.
//
// Only the explicit user-initiated save is tracked. autosaveProject() runs
// continuously on every mutation, so an event there would be noise, not a
// signal. No project name is sent: see the note in state/store.js viewInfo.
export function saveProject(project) {
  saveProjectToFile(project);
  trackProjectSave({
    target: 'file',
    session_mode: session.getMode(),
    script_count: project && project.scripts ? project.scripts.length : 0,
    board_count: project && project.boards ? project.boards.length : 0,
    link_count: project && project.links ? project.links.length : 0,
  });
}

export async function openProjectFile(file) {
  const text = await readFileAsText(file);
  return parseProjectFileText(text);
}

// Continuous persistence: routed by mode.
export function autosaveProject(project) {
  if (session.getMode() === 'remote') return saveProjectRemote(project);
  return saveCurrentProjectLocally(project);
}

export function loadAutosavedProject() {
  if (session.getMode() === 'remote') {
    const id = session.getCurrentRemoteId();
    // No remembered project: the start screen (signed in) offers the cloud list.
    return id ? loadProjectRemote(id) : Promise.resolve(null);
  }
  return loadCurrentProjectLocally();
}

// Deselects the open project. In remote mode this only forgets which project is
// open (it never deletes server data). It does NOT touch the browser-local slot:
// an account is required now, so that slot only ever holds a project written
// before that, still waiting for its owner to add it to their account (see
// peekLocalProject / adoptLocalProject), and going back to the home screen must
// not be what deletes it.
export function clearAutosavedProject() {
  if (session.getMode() === 'remote') {
    session.setCurrentRemoteId(null);
    session.setBase(null);
    session.setBaseSnapshot(null);
    return Promise.resolve();
  }
  return clearCurrentProjectLocally();
}

// A project left in this browser's local slot from before an account was
// required. Looked at, never consumed: nothing is deleted until a copy is safely
// in the account.
export function peekLocalProject() {
  return loadCurrentProjectLocally();
}

// Moves that project into the signed-in account and only then empties the local
// slot, so a failed upload leaves it exactly where it was. The open-project
// bookkeeping is cleared first because saveProjectRemote updates whatever
// project is remembered as open, and this must create a new one, never write
// over a cloud project the person had open last time.
export async function adoptLocalProject(project) {
  session.setCurrentRemoteId(null);
  session.setBase(null);
  session.setBaseSnapshot(null);
  await saveProjectRemote(project);
  await clearCurrentProjectLocally();
}

// Loading a project the user picked from their cloud list. Kept here (rather
// than importing the remote adapter into a component) so components only ever
// know about this seam.
export function loadRemoteProject(id) {
  return loadProjectRemote(id);
}

// Sharing goes through the same seam. Components import these from here, never
// from the adapter directly, so sharing swaps with the backend like everything
// else does.
export {
  listShares, addShare, removeShare,
  getReadLink, createReadLink, revokeReadLink,
  readLinkUrl,
} from './sharing-adapter.js';

export function loadSharedProjection(token) {
  return fetchSharedProjection(token);
}
